import { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@freelancer-os/ui/src/components/ui/badge';
import { Button } from '@freelancer-os/ui/src/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@freelancer-os/ui/src/components/ui/alert-dialog';
import type { ArtifactWithMeta, Invoice } from '@freelancer-os/shared-types';
import {
  AlertTriangle,
  Bell,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  MessageSquare,
  Pencil,
  Send,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { fetchApi } from '../../../lib/api';
import {
  useInvoiceByArtifact,
  useMarkShared,
} from '../../../hooks/useInvoices';
import { SendInvoiceDialog } from '../../invoices/components/SendInvoiceDialog';

const formatVND = (amount: number) =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);

interface ArtifactPanelProps {
  artifacts: ArtifactWithMeta[];
  threadId: string | null;
  onClose: () => void;
  onEditManual: (artifact: ArtifactWithMeta) => void;
}

export function ArtifactPanel({
  artifacts,
  threadId,
  onClose,
  onEditManual,
}: ArtifactPanelProps) {
  const { t } = useTranslation('chat');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Always default to the latest artifact. Browsing position is local state
  // only — URL sync was tried earlier but the persisted ?artifact=N kept
  // overriding "go to latest" on reload + thread switch + new stream.
  const [selectedIndex, setSelectedIndexState] = useState(() =>
    Math.max(0, artifacts.length - 1),
  );

  // Follow the latest artifact every time the array grows (initial thread
  // load OR new artifact streamed mid-conversation). User can step back with
  // the paginator arrows but a fresh stream yanks them to the latest, which
  // matches expectation that the most recent AI action gets focus.
  useEffect(() => {
    if (artifacts.length > 0) {
      setSelectedIndexState(artifacts.length - 1);
    }
  }, [artifacts.length]);

  const setSelectedIndex = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(artifacts.length - 1, next));
      setSelectedIndexState(clamped);
    },
    [artifacts.length],
  );

  const current = artifacts[selectedIndex];
  const currentArtifactIndex = current?.artifactIndex ?? null;

  const { data: invoice } = useInvoiceByArtifact(threadId, currentArtifactIndex);

  // QR is keyed by invoice.id so it survives reloads — replaces the previous
  // local useState approach that lost the QR on refresh. retry:false so a 400
  // (e.g. bank not linked) surfaces immediately instead of after 3 retries.
  const {
    data: qrData,
    error: qrErrorRaw,
    refetch: refetchQr,
    isFetching: qrFetching,
  } = useQuery<{ qrUrl: string; paymentRef: string }>({
    queryKey: ['invoices', invoice?.id, 'qr'],
    queryFn: () => fetchApi(`/invoices/${invoice!.id}/qr`),
    enabled: !!invoice?.id,
    staleTime: 60_000,
    retry: false,
  });
  const qrErrorCode = (qrErrorRaw as (Error & { code?: string }) | null)?.code ?? null;

  // Whether the org has linked a bank account — same source + cache key as the
  // Settings → Bank tab. Used to warn before approve (QR needs a bank account).
  const { data: bankAccounts } = useQuery<{ xid: string }[]>({
    queryKey: ['bank-accounts'],
    queryFn: () => fetchApi<{ xid: string }[]>('/bank-accounts'),
    staleTime: 5 * 60_000,
  });
  const bankLinked = (bankAccounts?.length ?? 0) > 0;

  const [approving, setApproving] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [markSharedConfirm, setMarkSharedConfirm] = useState(false);
  const [bankWarnOpen, setBankWarnOpen] = useState(false);

  const goLinkBank = useCallback(() => {
    navigate('/settings?tab=bank');
  }, [navigate]);

  const markShared = useMarkShared(invoice?.id ?? '');

  const handleApprove = useCallback(async () => {
    if (!current || !threadId) return;
    setApproving(true);
    try {
      await fetchApi<Invoice>('/invoices', {
        method: 'POST',
        body: JSON.stringify({
          clientId: current.data.invoiceDraft.clientId,
          items: current.data.invoiceDraft.items,
          taxRate: current.data.invoiceDraft.taxRate || 0,
          depositAmount: current.data.invoiceDraft.depositAmount || 0,
          notes: current.data.invoiceDraft.notes,
          artifactData: current.data,
          chatThreadId: threadId,
          artifactIndex: current.artifactIndex,
        }),
      });
      // Invalidate so useInvoiceByArtifact refetches and the footer flips
      // from "Phê duyệt" -> DRAFT outreach buttons immediately (was waiting
      // for the 15s staleTime before this fix).
      await queryClient.invalidateQueries({
        queryKey: ['invoices', 'by-artifact', threadId, current.artifactIndex],
      });
      // QR fetches itself via the useQuery above keyed by invoice.id.
    } catch (err) {
      console.error('Approve failed:', err);
      toast.error(t('artifact.approveFailed'));
    } finally {
      setApproving(false);
    }
  }, [current, threadId, t, queryClient]);

  // Guard the approve action: if no bank is linked, warn first — QR generation
  // needs a bank account. User can link now, or create the invoice without a QR
  // (it's recoverable: the QR endpoint is stateless and refetches once linked).
  const onApproveClick = useCallback(() => {
    if (!bankLinked) {
      setBankWarnOpen(true);
      return;
    }
    handleApprove();
  }, [bankLinked, handleApprove]);

  const handleMarkShared = useCallback(() => {
    if (!invoice) return;
    markShared.mutate(
      { channel: 'zalo' },
      {
        onSuccess: () => {
          setMarkSharedConfirm(false);
          toast.success(t('artifact.marked'));
        },
        onError: () => {
          toast.error(t('artifact.markFailed'));
        },
      },
    );
  }, [invoice, markShared, t]);

  if (artifacts.length === 0 || !current) {
    return <EmptyPanel onClose={onClose} />;
  }

  const status = invoice?.status ?? null;
  const isDraft = status === 'DRAFT';
  const isPendingLike = status === 'PENDING' || status === 'PARTIAL' || status === 'OVERDUE';
  const isClosed = status === 'PAID' || status === 'CANCELLED';
  const notApproved = !invoice;

  const label = invoice
    ? invoice.invoiceCode
    : t('artifact.draftLabel', { n: selectedIndex + 1 });

  return (
    <aside className="w-[400px] border-l border-surface-200 bg-surface-50 flex flex-col shrink-0 animate-[slide-in-right_0.3s_cubic-bezier(0.16,1,0.3,1)]">
      <div className="h-[56px] border-b border-surface-200 flex items-center justify-between px-3 bg-surface-0/80 backdrop-blur-sm shrink-0">
        <div className="flex items-center gap-1 min-w-0">
          <FileText className="w-4 h-4 text-brand-500 shrink-0" strokeWidth={2} />
          {artifacts.length > 1 ? (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setSelectedIndex(selectedIndex - 1)}
                disabled={selectedIndex === 0}
                aria-label={t('artifact.prev')}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-[12.5px] font-medium text-surface-800 truncate">
                {label}{' '}
                <span className="text-surface-400 tabular-nums">
                  ({selectedIndex + 1}/{artifacts.length})
                </span>
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setSelectedIndex(selectedIndex + 1)}
                disabled={selectedIndex === artifacts.length - 1}
                aria-label={t('artifact.next')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <span className="text-[13px] font-semibold text-surface-800 ml-1.5">
              {label}
            </span>
          )}
          {invoice ? (
            <Badge variant="outline" className="text-[10px] font-mono ml-1 shrink-0">
              {t(`status.${status}`, { defaultValue: status })}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-[10.5px] font-mono text-surface-400 bg-surface-100 border-surface-200 px-1.5 py-0.5 rounded-none ml-1 shrink-0"
            >
              {t('artifact.draft')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="h-8 w-8"
            title={t('artifact.close')}
          >
            <X className="w-4 h-4" strokeWidth={2} />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <InvoiceCard artifact={current} qrData={qrData ?? null} invoice={invoice ?? null} />
        {invoice && qrErrorCode && (
          <div className="mt-4 flex items-start gap-2 px-3 py-3 bg-amber-50 border-l-2 border-amber-400">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[12.5px] font-medium text-amber-800">
                {qrErrorCode === 'BANK_NOT_LINKED'
                  ? t('artifact.qrBankNotLinked')
                  : t('artifact.qrUnsupportedBank')}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {qrErrorCode === 'BANK_NOT_LINKED' && (
                  <Button
                    size="sm"
                    className="h-7 gap-1.5 text-[12px]"
                    onClick={goLinkBank}
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    {t('artifact.linkBank')}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 gap-1.5 text-[12px]"
                  disabled={qrFetching}
                  onClick={() => refetchQr()}
                >
                  {qrFetching ? t('artifact.qrRetrying') : t('artifact.qrRetry')}
                </Button>
              </div>
            </div>
          </div>
        )}
        {current.data.missingFields.length > 0 && (
          <div className="mt-4 flex items-start gap-2 px-3 py-3 bg-amber-50 border-l-2 border-amber-400">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-[12.5px] font-medium text-amber-800">
                {t('artifact.missingInfo')}
              </p>
              <ul className="mt-1 space-y-0.5">
                {current.data.missingFields.map((field) => (
                  <li key={field} className="text-[12px] text-amber-700">
                    • {field}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-surface-200 bg-surface-0 shrink-0 space-y-2">
        {notApproved && (
          <>
            <Button
              className="w-full gap-2 py-2.5 h-auto bg-brand-500 hover:bg-brand-600 text-[13.5px] font-semibold"
              disabled={approving || !current.data.invoiceDraft.clientId}
              onClick={onApproveClick}
            >
              <Check className="w-4 h-4" strokeWidth={2} />
              {approving ? t('artifact.approving') : t('actions.approveQR')}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 gap-1.5 py-2 h-auto text-[12.5px] font-medium"
                onClick={() => onEditManual(current)}
              >
                <Pencil className="w-3.5 h-3.5" />
                {t('artifact.editManual')}
              </Button>
              <Button
                variant="outline"
                className="flex-1 gap-1.5 py-2 h-auto text-[12.5px] font-medium"
              >
                <Download className="w-3.5 h-3.5" />
                {t('artifact.downloadPDF')}
              </Button>
            </div>
          </>
        )}

        {invoice && isDraft && (
          <>
            <Button
              className="w-full gap-2 py-2.5 h-auto bg-brand-500 hover:bg-brand-600 text-[13.5px] font-semibold"
              onClick={() => setSendOpen(true)}
            >
              <Send className="w-4 h-4" strokeWidth={2} />
              {t('artifact.sendEmail')}
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1 gap-1.5 py-2 h-auto text-[12.5px] font-medium"
                onClick={() => setMarkSharedConfirm(true)}
              >
                <MessageSquare className="w-3.5 h-3.5" />
                {t('artifact.markZalo')}
              </Button>
              <Button
                variant="outline"
                className="flex-1 gap-1.5 py-2 h-auto text-[12.5px] font-medium"
                onClick={() => window.open(`/invoices/${invoice.id}`, '_blank')}
              >
                <ExternalLink className="w-3.5 h-3.5" />
                {t('artifact.openInvoice')}
              </Button>
            </div>
          </>
        )}

        {invoice && isPendingLike && (
          <>
            <Button
              variant="outline"
              className="w-full gap-2 py-2.5 h-auto text-[13.5px] font-semibold"
              onClick={() => setReminderOpen(true)}
            >
              <Bell className="w-4 h-4" />
              {t('artifact.sendReminder')}
            </Button>
            <Button
              variant="ghost"
              className="w-full gap-2 py-2 h-auto text-[12.5px] text-surface-600"
              onClick={() => window.open(`/invoices/${invoice.id}`, '_blank')}
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {t('artifact.openInvoice')}
            </Button>
          </>
        )}

        {invoice && isClosed && (
          <Button
            variant="ghost"
            className="w-full gap-2 py-2 h-auto text-[12.5px] text-surface-600"
            onClick={() => window.open(`/invoices/${invoice.id}`, '_blank')}
          >
            <ExternalLink className="w-3.5 h-3.5" />
            {t('artifact.openInvoice')}
          </Button>
        )}
      </div>

      {invoice && (
        <>
          <SendInvoiceDialog
            invoice={invoice}
            mode="send"
            open={sendOpen}
            onOpenChange={setSendOpen}
          />
          <SendInvoiceDialog
            invoice={invoice}
            mode="reminder"
            open={reminderOpen}
            onOpenChange={setReminderOpen}
          />
        </>
      )}

      <AlertDialog open={markSharedConfirm} onOpenChange={setMarkSharedConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('artifact.markZaloConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('artifact.markZaloConfirm.body')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('artifact.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkShared}>
              {t('artifact.markZaloConfirm.action')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bankWarnOpen} onOpenChange={setBankWarnOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('artifact.bankWarn.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('artifact.bankWarn.body')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setBankWarnOpen(false);
                handleApprove();
              }}
            >
              {t('artifact.bankWarn.proceed')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={goLinkBank}>
              {t('artifact.bankWarn.link')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}

function EmptyPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('chat');
  return (
    <aside className="w-[400px] border-l border-surface-200 bg-surface-50 flex flex-col shrink-0">
      <div className="h-[56px] border-b border-surface-200 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-brand-500" strokeWidth={2} />
          <span className="text-[13.5px] font-semibold text-surface-800">
            {t('artifact.title')}
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
          <X className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <FileText className="w-12 h-12 text-surface-200 mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[13px] text-surface-400">{t('artifact.emptyTitle')}</p>
          <p className="text-[12px] text-surface-300 mt-1">{t('artifact.emptySubtitle')}</p>
        </div>
      </div>
    </aside>
  );
}

function InvoiceCard({
  artifact,
  qrData,
  invoice,
}: {
  artifact: ArtifactWithMeta;
  qrData: { qrUrl: string; paymentRef: string } | null;
  invoice: Invoice | null;
}) {
  const { t } = useTranslation('chat');
  const { invoiceDraft, matchedClient } = artifact.data;
  const items = invoiceDraft.items;

  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const taxAmount = subtotal * (invoiceDraft.taxRate / 100);
  const total = subtotal + taxAmount;
  const remaining = total - invoiceDraft.depositAmount;

  const clientName = matchedClient?.name || t('artifact.unknownClient');
  const clientEmail = matchedClient?.email || '';
  const clientInitial = clientName.charAt(0).toUpperCase();

  const today = new Date();
  const dateStr = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const effectivePaymentRef = invoice?.paymentRef ?? qrData?.paymentRef ?? null;
  const effectiveQrUrl = qrData?.qrUrl ?? null;

  return (
    <div className="bg-surface-0 border border-surface-200 shadow-sm overflow-hidden">
      <div className="bg-surface-900 px-5 py-5 text-white">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-surface-400">
              {t('artifact.invoice')}
            </div>
            <div className="text-[24px] font-bold mt-1 tracking-tight font-mono">
              {invoice?.invoiceCode ?? t('artifact.draft')}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-surface-400">
              {t('artifact.createdDate')}
            </div>
            <div className="text-[13px] font-semibold font-mono mt-1">{dateStr}</div>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-surface-100">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-surface-400 mb-2">
          {t('artifact.client')}
        </div>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-[13px]">
            {clientInitial}
          </div>
          <div>
            <div className="text-[14px] font-semibold text-surface-800">{clientName}</div>
            {clientEmail && (
              <div className="text-[12px] text-surface-400 font-mono">{clientEmail}</div>
            )}
          </div>
        </div>
      </div>

      <div className="px-5 py-4">
        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-surface-400 mb-3">
          {t('artifact.details')}
        </div>
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i}>
              {i > 0 && <div className="h-px bg-surface-100 mb-3" />}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-[13.5px] font-medium text-surface-800">
                    {item.description}
                  </div>
                  <div className="text-[12px] text-surface-400 font-mono mt-0.5">
                    {item.quantity} × {formatVND(item.unitPrice)}
                  </div>
                </div>
                <div className="text-[13.5px] font-semibold text-surface-800 font-mono">
                  {formatVND(item.quantity * item.unitPrice)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="px-5 py-4 bg-surface-50 border-t border-surface-200">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-[13px]">
            <span className="text-surface-500">{t('artifact.subtotal')}</span>
            <span className="text-surface-700 font-mono font-medium">
              {formatVND(subtotal)}
            </span>
          </div>
          {invoiceDraft.taxRate > 0 && (
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-surface-500">
                {t('artifact.vat', { rate: invoiceDraft.taxRate })}
              </span>
              <span className="text-surface-700 font-mono font-medium">
                {formatVND(taxAmount)}
              </span>
            </div>
          )}
          {invoiceDraft.depositAmount > 0 && (
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-surface-500">{t('artifact.deposit')}</span>
              <span className="text-brand-600 font-mono font-semibold">
                −{formatVND(invoiceDraft.depositAmount)}
              </span>
            </div>
          )}
          <div className="h-[2px] bg-surface-900 my-2" />
          <div className="flex items-center justify-between">
            <span className="text-[14px] font-bold uppercase tracking-wider text-surface-800">
              {t('artifact.balance')}
            </span>
            <span className="text-[20px] font-bold text-surface-900 font-mono tracking-tight">
              {formatVND(remaining)}
            </span>
          </div>
        </div>
      </div>

      {(effectiveQrUrl || effectivePaymentRef) && (
        <div className="px-5 py-4 border-t border-surface-200">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-surface-400 mb-3">
            {t('artifact.vietqr')}
          </div>
          {effectiveQrUrl ? (
            <div className="text-center">
              <img src={effectiveQrUrl} alt="VietQR" className="w-full rounded border" />
              {effectivePaymentRef && (
                <p className="text-[11px] text-surface-500 mt-2">
                  {t('artifact.paymentRef')}{' '}
                  <span className="font-mono font-bold text-surface-700">
                    {effectivePaymentRef}
                  </span>
                </p>
              )}
            </div>
          ) : (
            <p className="text-[12px] text-surface-500 text-center py-4">
              {t('artifact.paymentRef')}{' '}
              <span className="font-mono font-bold text-surface-700">
                {effectivePaymentRef}
              </span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
